import {
  resolveNotificationInternalPath,
  resolveNotificationReportsWizardPath,
} from "@/utils/notificationDetailNavigation"

export type NotificationDetailAction = {
  id: string
  rowLabel: string
  buttonLabel: string
  /** In-app router path, or absolute URL when `external` is true. */
  path: string
  /** Open via full navigation / new tab instead of react-router. */
  external?: boolean
}

function pickDetailUrl(data: Record<string, unknown>, keys: readonly string[]): string | null {
  for (const key of keys) {
    const value = data[key]
    if (typeof value === "string" && value.trim()) {
      return value.trim()
    }
  }
  return null
}

const NOTIFICATION_DETAIL_ACTION_DEFINITIONS = [
  {
    id: "statistics",
    rowLabel: "Statistics",
    buttonLabel: "View statistics",
    keys: ["statistics_url", "statisticsUrl", "action_url", "actionUrl"] as const,
    resolvePath: resolveNotificationInternalPath,
  },
  {
    id: "trade-review",
    rowLabel: "Trade review",
    buttonLabel: "Open trade review",
    keys: ["trade_review_url", "tradeReviewUrl"] as const,
    resolvePath: resolveNotificationInternalPath,
  },
  {
    id: "reports",
    rowLabel: "Report",
    buttonLabel: "Generate report",
    keys: ["reports_url", "reportsUrl"] as const,
    resolvePath: resolveNotificationReportsWizardPath,
  },
] as const

/** Structured deep links from notification `data` / `details` (not email markdown). */
export function buildNotificationDetailActions(
  details: unknown
): NotificationDetailAction[] {
  if (!details || typeof details !== "object" || Array.isArray(details)) {
    return []
  }

  const data = details as Record<string, unknown>
  const actions: NotificationDetailAction[] = []

  for (const definition of NOTIFICATION_DETAIL_ACTION_DEFINITIONS) {
    const url = pickDetailUrl(data, definition.keys)
    if (!url) continue

    const path = definition.resolvePath(url)
    if (!path) continue

    actions.push({
      id: definition.id,
      rowLabel: definition.rowLabel,
      buttonLabel: definition.buttonLabel,
      path,
    })
  }

  return actions
}

/** Email `body_markdown` follow-up copy — rendered as in-app buttons instead. */
export function isNotificationSuggestedNextStepsMarkdown(message: string): boolean {
  return /^What to do next\s*\(optional\)\s*:/i.test(message.trim())
}

export function stripNotificationSuggestedNextStepsMarkdown(message: string): string {
  if (isNotificationSuggestedNextStepsMarkdown(message)) {
    return ""
  }
  return message.trim()
}
